import { z } from 'zod';
/**
 * Plain-language messages for every schema rule, written for the person
 * filling in the form rather than for a developer.
 *
 * Each message reads on its own under the field it belongs to, so it never
 * repeats the field's name. A rule that carries its own message in the schema
 * (a GSTIN's "Enter a 15-character GSTIN…") keeps it: Zod only falls back to this map when the
 * schema says nothing.
 */
export const plainErrorMap = (issue, ctx) => {
    switch (issue.code) {
        case z.ZodIssueCode.invalid_type:
            if (issue.received === 'undefined' || issue.received === 'null') {
                return { message: 'This is required' };
            }
            if (issue.expected === 'number' || issue.expected === 'integer') {
                return { message: 'Enter a number' };
            }
            if (issue.expected === 'date')
                return { message: 'Enter a valid date' };
            return { message: 'This value is not valid here' };
        case z.ZodIssueCode.too_small: {
            const min = Number(issue.minimum);
            if (issue.type === 'string') {
                return { message: min <= 1 ? 'This is required' : `Use at least ${min} characters` };
            }
            if (issue.type === 'array' || issue.type === 'set') {
                return { message: min <= 1 ? 'Choose at least one' : `Choose at least ${min}` };
            }
            if (issue.type === 'number' || issue.type === 'bigint') {
                return {
                    message: issue.inclusive ? `Must be ${min} or more` : `Must be more than ${min}`,
                };
            }
            if (issue.type === 'date')
                return { message: 'This date is too early' };
            return { message: ctx.defaultError };
        }
        case z.ZodIssueCode.too_big: {
            const max = Number(issue.maximum);
            if (issue.type === 'string')
                return { message: `Use at most ${max} characters` };
            if (issue.type === 'array' || issue.type === 'set') {
                return { message: `Choose at most ${max}` };
            }
            if (issue.type === 'number' || issue.type === 'bigint') {
                return { message: issue.inclusive ? `Must be ${max} or less` : `Must be less than ${max}` };
            }
            if (issue.type === 'date')
                return { message: 'This date is too late' };
            return { message: ctx.defaultError };
        }
        case z.ZodIssueCode.invalid_string:
            if (issue.validation === 'email')
                return { message: 'Enter a valid email address' };
            if (issue.validation === 'url')
                return { message: 'Enter a valid web address' };
            if (issue.validation === 'datetime' || issue.validation === 'date') {
                return { message: 'Enter a valid date' };
            }
            return { message: 'This is not in the right format' };
        case z.ZodIssueCode.invalid_enum_value:
        case z.ZodIssueCode.invalid_literal:
        case z.ZodIssueCode.invalid_union:
        case z.ZodIssueCode.invalid_union_discriminator:
            return { message: 'Choose one of the options' };
        case z.ZodIssueCode.invalid_date:
            return { message: 'Enter a valid date' };
        case z.ZodIssueCode.not_multiple_of:
            return { message: `Must be a multiple of ${String(issue.multipleOf)}` };
        case z.ZodIssueCode.unrecognized_keys:
            return { message: 'The form sent something this screen does not accept' };
        default:
            return { message: ctx.defaultError };
    }
};
/**
 * The plain-language message for an issue that has already been raised, e.g.
 * by a `schema.parse` inside a service. A message the schema wrote itself is
 * kept; only Zod's generic wording is replaced.
 */
export function plainIssueMessage(issue) {
    const ctx = { defaultError: issue.message, data: undefined };
    const generic = z.defaultErrorMap(issue, ctx).message;
    return issue.message === generic ? plainErrorMap(issue, ctx).message : issue.message;
}
/** Every issue of a failed parse, as `{ path, message }` in plain language. */
export function plainIssues(error) {
    return error.issues.map((issue) => ({
        path: issue.path.join('.'),
        message: plainIssueMessage(issue),
    }));
}
/** Check `value` against `schema` with plain-language messages, field by field. */
export function fieldProblems(schema, value) {
    const result = schema.safeParse(value);
    return result.success ? [] : plainIssues(result.error);
}
//# sourceMappingURL=validation.js.map